import { ComponentProps, useEffect, useRef } from 'react';
import { Box, ModalInfo, theme } from '@bpba/ui-components';

// Workaround del ModalInfo de @bpba/ui-components (verificado en 1.8.0, persiste en 2.6.0):
// La version publicada llama `onClose` ante cualquier click sobre el overlay
// (sin chequear target===currentTarget), asi que clickear el body o la cabecera
// cierra el modal. Y ademas cierra con Escape.
//
// Se evaluo eliminar este wrapper al migrar a 2.6.0 pero el comportamiento
// sigue igual — cualquier click interno dispara onClose. Mientras ModalInfo
// no filtre target===currentTarget internamente, este shim es necesario.
//
// Politica: solo cierra con X o click en el backdrop (afuera).
//
// Implementacion: trackeamos el ultimo mousedown via listener capture en
// window. Cuando ModalInfo llama a nuestro `handleClose`, chequeamos si el
// mousedown reciente fue sobre el overlay (backdrop) o sobre el boton de
// cierre. Si si, dejamos que se cierre; si no, lo bloqueamos.
// Para cierres programaticos (ej. DNI verificado OK) no hay mousedown reciente,
// asi que pasan derecho. Escape se bloquea con stopImmediatePropagation en
// capture phase.
//
// Por que no usar addEventListener stop en el Window del modal: React 17+ usa
// delegacion en el root, asi que cortar la propagacion nativa antes de que
// llegue al root mata TODOS los handlers React de elementos internos
// (incluido el onClick del boton Continuar).
export const SafeModalInfo = ({
  children,
  open,
  onClose,
  closable = true,
  ...rest
}: ComponentProps<typeof ModalInfo>) => {
  const lastMouseDownEl = useRef<HTMLElement | null>(null);
  const lastMouseDownTime = useRef(0);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      lastMouseDownEl.current = e.target as HTMLElement;
      lastMouseDownTime.current = performance.now();
    };
    const blockEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') e.stopImmediatePropagation();
    };
    window.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('keydown', blockEscape, true);
    return () => {
      window.removeEventListener('mousedown', onMouseDown, true);
      window.removeEventListener('keydown', blockEscape, true);
    };
  }, [open]);

  const handleClose = () => {
    const recent = performance.now() - lastMouseDownTime.current < 250;
    if (!recent) {
      // Cierre programatico — dejamos pasar.
      onClose?.();
      return;
    }
    const el = lastMouseDownEl.current;
    if (!el) return;
    const closestBtn = el.closest('button');
    const isCloseBtn = closestBtn !== null && (
      /cerrar|close/i.test(closestBtn.getAttribute('aria-label') ?? '') ||
      /cerrar/i.test(closestBtn.textContent ?? '')
    );
    const isBackdrop = el.classList?.contains('modal-ui');
    if (isBackdrop || isCloseBtn) {
      onClose?.();
    }
  };

  return (
    <ModalInfo open={open} onClose={closable ? handleClose : undefined} closable={closable} {...rest}>
      {/* pt para separar el mensaje del titulo del modal — patron aplicado
          en todos los modales de la app. */}
      <Box pt={`${theme.space[4]}px`}>{children}</Box>
    </ModalInfo>
  );
};
